"use client"

import * as React from "react"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Account } from "@/types/workspace"
import { Briefcase, Search, X, Check, Plus, Building2, ArrowLeft, MapPin, FileText, Tag, AlertCircle } from "lucide-react"
import { toast } from "sonner"

// Validação de dígitos verificadores de CNPJ brasileiro
function isValidCNPJ(cnpj: string): boolean {
  const clean = cnpj.replace(/\D/g, "")
  if (clean.length !== 14) return false
  if (/^(\d)\1{13}$/.test(clean)) return false

  let size = clean.length - 2
  let numbers = clean.substring(0, size)
  const digits = clean.substring(size)
  let sum = 0
  let pos = size - 7
  for (let i = size; i >= 1; i--) {
    sum += Number(numbers.charAt(size - i)) * pos--
    if (pos < 2) pos = 9
  }
  let result = sum % 11 < 2 ? 0 : 11 - (sum % 11)
  if (result !== Number(digits.charAt(0))) return false

  size = size + 1
  numbers = clean.substring(0, size)
  sum = 0
  pos = size - 7
  for (let i = size; i >= 1; i--) {
    sum += Number(numbers.charAt(size - i)) * pos--
    if (pos < 2) pos = 9
  }
  result = sum % 11 < 2 ? 0 : 11 - (sum % 11)
  if (result !== Number(digits.charAt(1))) return false

  return true
}

// Máscara automática de CNPJ (00.000.000/0000-00)
function maskCNPJ(value: string): string {
  const digits = value.replace(/\D/g, "").slice(0, 14)
  if (digits.length <= 2) return digits
  if (digits.length <= 5) return `${digits.slice(0, 2)}.${digits.slice(2)}`
  if (digits.length <= 8) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5)}`
  if (digits.length <= 12) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8)}`
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12, 14)}`
}

interface SwitchAccountModalProps {
  isOpen: boolean
  onClose: () => void
  accounts: Account[]
  currentAccount: Account | null
  onSwitchAccount: (accountId: string) => void
  t: Record<string, string>
  initialMode?: "list" | "create"
  hideBackButton?: boolean
  hideCancelButton?: boolean
}

export function SwitchAccountModal({
  isOpen,
  onClose,
  accounts,
  currentAccount,
  onSwitchAccount,
  t,
  initialMode = "list",
  hideBackButton = false,
  hideCancelButton = false
}: SwitchAccountModalProps) {
  const [mode, setMode] = React.useState<"list" | "create">(initialMode)
  const [searchQuery, setSearchQuery] = React.useState("")

  // Form states for creating a new company
  const [companyName, setCompanyName] = React.useState("")
  const [slug, setSlug] = React.useState("")
  const [companyDocument, setCompanyDocument] = React.useState("")
  const [category, setCategory] = React.useState("")
  const [location, setLocation] = React.useState("")
  const [bio, setBio] = React.useState("")
  const [isCreating, setIsCreating] = React.useState(false)

  // Validações auxiliares e listas filtradas (devem ser chamadas antes de qualquer early return)
  const isSlugTaken = React.useMemo(() => {
    if (!slug.trim()) return false
    return (accounts || []).some(a => a.slug?.toLowerCase() === slug.trim().toLowerCase())
  }, [slug, accounts])

  const filteredAccounts = React.useMemo(() => {
    return (accounts || []).filter(acc =>
      acc.name.toLowerCase().includes(searchQuery.toLowerCase())
    )
  }, [accounts, searchQuery])

  // Sync mode based on accounts availability or initialMode on open
  React.useEffect(() => {
    if (isOpen) {
      if (initialMode === "create" || !accounts || accounts.length === 0) {
        setMode("create")
      } else {
        setMode("list")
      }
      setSearchQuery("")
      setCompanyName("")
      setSlug("")
      setCompanyDocument("")
      setCategory("")
      setLocation("")
      setBio("")
    }
  }, [isOpen, accounts, initialMode])

  const handleNameChange = (val: string) => {
    setCompanyName(val)
    if (!slug || slug === "" || slug === companyName.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)+/g, "")) {
      const generatedSlug = val
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)+/g, "")
      setSlug(generatedSlug)
    }
  }

  const handleSlugChange = (val: string) => {
    const formatted = val
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9-]/g, "")
    setSlug(formatted)
  }

  const handleDocumentChange = (val: string) => {
    setCompanyDocument(maskCNPJ(val))
  }

  const cleanCnpjDigits = companyDocument.replace(/\D/g, "")
  const isCnpjComplete = cleanCnpjDigits.length === 14
  const isCnpjValid = cleanCnpjDigits.length > 0 ? isValidCNPJ(companyDocument) : true

  const handleCreateAccount = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!companyName.trim()) {
      toast.error("O nome da empresa é obrigatório.")
      return
    }

    if (!slug.trim()) {
      toast.error("O identificador (slug) da empresa é obrigatório.")
      return
    }

    if (slug.trim().length < 2) {
      toast.error("O identificador (slug) deve ter no mínimo 2 caracteres.")
      return
    }

    if (isSlugTaken) {
      toast.error(`O identificador "@${slug.trim()}" já está em uso. Escolha outro slug.`)
      return
    }

    if (!companyDocument.trim() || !isCnpjComplete || !isValidCNPJ(companyDocument)) {
      toast.error("Informe um CNPJ válido com 14 dígitos.")
      return
    }

    const token = typeof document !== "undefined"
      ? document.cookie
        .split("; ")
        .find((row) => row.startsWith("token="))
        ?.split("=")[1] || (typeof localStorage !== "undefined" ? localStorage.getItem("token") || localStorage.getItem("bold_token") : null)
      : null

    if (!token) {
      toast.error("Sessão expirada. Faça login novamente.")
      return
    }

    setIsCreating(true)
    try {
      const res = await fetch("/api/graphql", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          query: `
            mutation CreateAccount($input: CreateAccountInput!) {
              createAccount(input: $input) {
                token
                currentAccount {
                  id
                  name
                  slug
                  logoUrl
                  document
                  category
                  bio
                  location
                }
              }
            }
          `,
          variables: {
            input: {
              name: companyName.trim(),
              slug: slug.trim(),
              document: companyDocument.trim(),
              category: category.trim() || undefined,
              bio: bio.trim() || undefined,
              location: location.trim() || undefined
            }
          }
        })
      })

      const result = await res.json()
      if (result.data && result.data.createAccount) {
        const { token: newToken, currentAccount: createdAcc } = result.data.createAccount
        if (typeof document !== "undefined") {
          document.cookie = `token=${newToken}; path=/; max-age=604800; SameSite=Lax`
        }
        if (typeof localStorage !== "undefined") {
          localStorage.setItem("token", newToken)
          localStorage.setItem("bold_token", newToken)
          localStorage.setItem("currentAccount", JSON.stringify(createdAcc))
        }
        toast.success(`Empresa "${createdAcc.name}" criada com sucesso!`)
        onClose()
        onSwitchAccount(createdAcc.id)
      } else if (result.errors && result.errors[0]) {
        toast.error(result.errors[0].message || "Erro ao criar empresa")
      }
    } catch (err) {
      console.error(err)
      toast.error("Erro de conexão ao criar empresa")
    } finally {
      setIsCreating(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-55 flex items-center justify-center bg-black/60 backdrop-blur-xs p-4 font-sans animate-in fade-in duration-150">
      <Card className="w-full max-w-lg border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-6 shadow-2xl rounded-2xl space-y-4 font-sans max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center pb-3 border-b border-zinc-100 dark:border-zinc-800">
          <div className="flex items-center gap-2">
            {mode === "create" && accounts && accounts.length > 0 && !hideBackButton && (
              <button
                type="button"
                onClick={() => setMode("list")}
                className="text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 p-1 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors cursor-pointer mr-1"
                title="Voltar para a lista"
              >
                <ArrowLeft className="size-4" />
              </button>
            )}
            <div className="size-8 rounded-lg bg-blue-50 dark:bg-blue-950/50 text-[#004EB1] dark:text-blue-400 flex items-center justify-center shrink-0">
              {mode === "create" ? <Building2 className="size-4.5" /> : <Briefcase className="size-4.5" />}
            </div>
            <div>
              <h3 className="text-sm font-bold text-zinc-900 dark:text-white leading-tight">
                {mode === "create"
                  ? "Criar Nova Empresa"
                  : (t.switchAccountTitle || "Trocar de Empresa")}
              </h3>
              <p className="text-[11px] text-zinc-500 dark:text-zinc-400 leading-tight mt-0.5">
                {mode === "create"
                  ? "Preencha as informações para configurar uma nova organização"
                  : "Selecione a empresa para alternar o espaço de trabalho"}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-1">
            <button
              onClick={onClose}
              className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 cursor-pointer p-1.5 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
            >
              <X className="size-4" />
            </button>
          </div>
        </div>

        {/* Mode: List Accounts */}
        {mode === "list" && (
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-zinc-400" />
              <input
                type="text"
                placeholder={t.searchAccount || "Pesquisar empresa..."}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full rounded-xl border border-zinc-200 dark:border-zinc-800 pl-9 pr-3 py-2 text-xs focus-visible:border-[#004EB1] focus-visible:outline-none dark:bg-zinc-950 dark:text-white font-sans bg-zinc-50 dark:bg-zinc-950"
              />
            </div>

            <div className="max-h-64 overflow-y-auto space-y-1.5 pr-1">
              {filteredAccounts.length === 0 ? (
                <div className="text-center py-6 px-4 bg-zinc-50 dark:bg-zinc-950/50 rounded-xl border border-dashed border-zinc-200 dark:border-zinc-800">
                  <Briefcase className="size-8 text-zinc-300 dark:text-zinc-600 mx-auto mb-2" />
                  <p className="text-xs font-semibold text-zinc-600 dark:text-zinc-400">Nenhuma empresa encontrada</p>
                  <p className="text-[11px] text-zinc-400 mt-1">Crie uma nova empresa para começar a gerenciar seus workspaces.</p>
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => setMode("create")}
                    className="mt-3 text-xs bg-[#004EB1] hover:bg-blue-700 text-white font-semibold cursor-pointer h-7"
                  >
                    <Plus className="size-3.5 mr-1" />
                    Criar Empresa
                  </Button>
                </div>
              ) : (
                filteredAccounts.map((acc) => {
                  const isActive = acc.id === currentAccount?.id
                  return (
                    <button
                      key={acc.id}
                      type="button"
                      onClick={() => {
                        onSwitchAccount(acc.id)
                        onClose()
                      }}
                      className={`w-full text-left p-3 rounded-xl text-xs font-semibold flex items-center justify-between transition-all border cursor-pointer ${isActive
                        ? "bg-[#004EB1]/5 border-[#004EB1]/40 text-[#004EB1] dark:bg-blue-950/40 dark:border-blue-500/40 dark:text-blue-400 font-bold shadow-xs"
                        : "bg-white dark:bg-zinc-950 border-zinc-200/80 dark:border-zinc-800 text-zinc-700 hover:bg-zinc-50 dark:text-zinc-300 dark:hover:bg-zinc-800/80 hover:text-zinc-900 dark:hover:text-white"
                        }`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className={`size-8 rounded-lg flex items-center justify-center font-bold text-xs shrink-0 ${isActive
                          ? "bg-[#004EB1] text-white"
                          : "bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300"
                          }`}>
                          {acc.name ? acc.name.charAt(0).toUpperCase() : "E"}
                        </div>
                        <div className="truncate">
                          <p className="truncate font-semibold leading-tight">{acc.name}</p>
                          <p className="text-[10px] text-zinc-400 font-normal truncate mt-0.5">
                            {acc.slug ? `@${acc.slug}` : "Sem slug"}
                            {acc.category ? ` • ${acc.category}` : ""}
                          </p>
                        </div>
                      </div>

                      {isActive && (
                        <span className="px-2 py-0.5 rounded-full bg-[#004EB1] text-white text-[9px] font-bold dark:bg-blue-600 flex items-center gap-1 select-none shrink-0">
                          <Check className="size-2.5 text-white" />
                          {t.activeAccountLabel || "Ativo"}
                        </span>
                      )}
                    </button>
                  )
                })
              )}
            </div>

            <div className="pt-2 border-t border-zinc-100 dark:border-zinc-800 flex justify-end">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setMode("create")}
                className="w-full text-xs font-semibold h-8.5 gap-1.5 border-dashed border-zinc-300 dark:border-zinc-700 text-zinc-700 dark:text-zinc-300 hover:text-[#004EB1] dark:hover:text-blue-400 hover:border-blue-300 dark:hover:border-blue-700 cursor-pointer"
              >
                <Plus className="size-3.5" />
                Cadastrar Nova Empresa
              </Button>
            </div>
          </div>
        )}

        {/* Mode: Create New Account Form */}
        {mode === "create" && (
          <form onSubmit={handleCreateAccount} className="space-y-3.5">
            <div className="space-y-1">
              <label className="text-xs font-bold text-zinc-700 dark:text-zinc-300 flex items-center gap-1">
                Nome da Empresa <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 size-3.5 text-zinc-400" />
                <input
                  type="text"
                  required
                  placeholder="Ex: Minha Empresa Tecnologia"
                  value={companyName}
                  onChange={(e) => handleNameChange(e.target.value)}
                  className="w-full rounded-lg border border-zinc-200 dark:border-zinc-800 pl-9 pr-3 py-2 text-xs focus-visible:border-[#004EB1] focus-visible:outline-none dark:bg-zinc-950 dark:text-white bg-zinc-50 dark:bg-zinc-950 font-medium"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-[11px] font-semibold text-zinc-700 dark:text-zinc-300 flex items-center gap-1">
                  Identificador / Slug <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-mono text-zinc-400">@</span>
                  <input
                    type="text"
                    required
                    placeholder="minha-empresa"
                    value={slug}
                    onChange={(e) => handleSlugChange(e.target.value)}
                    className={`w-full rounded-lg border pl-7 pr-3 py-1.5 text-xs font-mono focus-visible:outline-none dark:bg-zinc-950 dark:text-white bg-zinc-50 dark:bg-zinc-950 ${isSlugTaken
                        ? "border-red-500 focus-visible:border-red-500"
                        : "border-zinc-200 dark:border-zinc-800 focus-visible:border-[#004EB1]"
                      }`}
                  />
                </div>
                {isSlugTaken && (
                  <p className="text-[10px] text-red-500 flex items-center gap-1 mt-0.5">
                    <AlertCircle className="size-3 shrink-0" />
                    Este slug já está em uso por outra empresa.
                  </p>
                )}
              </div>

              <div className="space-y-1">
                <label className="text-[11px] font-semibold text-zinc-700 dark:text-zinc-300 flex items-center gap-1">
                  CNPJ <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <FileText className="absolute left-3 top-1/2 -translate-y-1/2 size-3.5 text-zinc-400" />
                  <input
                    type="text"
                    required
                    placeholder="00.000.000/0000-00"
                    value={companyDocument}
                    onChange={(e) => handleDocumentChange(e.target.value)}
                    maxLength={18}
                    className={`w-full rounded-lg border pl-9 pr-3 py-1.5 text-xs font-mono focus-visible:outline-none dark:bg-zinc-950 dark:text-white bg-zinc-50 dark:bg-zinc-950 ${companyDocument.length > 0 && (!isCnpjComplete || !isCnpjValid)
                        ? "border-red-500 focus-visible:border-red-500"
                        : "border-zinc-200 dark:border-zinc-800 focus-visible:border-[#004EB1]"
                      }`}
                  />
                </div>
                {companyDocument.length > 0 && (!isCnpjComplete || !isCnpjValid) && (
                  <p className="text-[10px] text-red-500 flex items-center gap-1 mt-0.5">
                    <AlertCircle className="size-3 shrink-0" />
                    CNPJ inválido (deve conter 14 dígitos válidos).
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-[11px] font-semibold text-zinc-700 dark:text-zinc-300 flex items-center gap-1">
                  Segmento / Categoria
                </label>
                <div className="relative">
                  <Tag className="absolute left-3 top-1/2 -translate-y-1/2 size-3.5 text-zinc-400" />
                  <input
                    type="text"
                    placeholder="Ex: Tecnologia, Consultoria..."
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="w-full rounded-lg border border-zinc-200 dark:border-zinc-800 pl-9 pr-3 py-1.5 text-xs focus-visible:border-[#004EB1] focus-visible:outline-none dark:bg-zinc-950 dark:text-white bg-zinc-50 dark:bg-zinc-950"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-[11px] font-semibold text-zinc-700 dark:text-zinc-300 flex items-center gap-1">
                  Localização / Cidade
                </label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 size-3.5 text-zinc-400" />
                  <input
                    type="text"
                    placeholder="Ex: São Paulo, SP"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    className="w-full rounded-lg border border-zinc-200 dark:border-zinc-800 pl-9 pr-3 py-1.5 text-xs focus-visible:border-[#004EB1] focus-visible:outline-none dark:bg-zinc-950 dark:text-white bg-zinc-50 dark:bg-zinc-950"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-[11px] font-semibold text-zinc-700 dark:text-zinc-300 flex items-center gap-1">
                Descrição Breve (Bio)
              </label>
              <textarea
                rows={2}
                placeholder="Conte um pouco sobre as atividades e objetivos da organização..."
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                className="w-full rounded-lg border border-zinc-200 dark:border-zinc-800 p-2.5 text-xs focus-visible:border-[#004EB1] focus-visible:outline-none dark:bg-zinc-950 dark:text-white bg-zinc-50 dark:bg-zinc-950 resize-none"
              />
            </div>

            <div className="flex items-center justify-end gap-2 pt-3 border-t border-zinc-100 dark:border-zinc-800">
              {accounts && accounts.length > 0 && !hideCancelButton && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setMode("list")}
                  disabled={isCreating}
                  className="text-xs h-8 px-3 cursor-pointer"
                >
                  Cancelar
                </Button>
              )}
              <Button
                type="submit"
                size="sm"
                disabled={isCreating || !companyName.trim() || !slug.trim() || isSlugTaken || !isCnpjComplete || !isCnpjValid}
                className="bg-[#004EB1] hover:bg-blue-700 text-white text-xs font-semibold h-8 px-4 cursor-pointer gap-1.5 disabled:opacity-50"
              >
                {isCreating ? "Criando..." : "Criar Empresa"}
              </Button>
            </div>
          </form>
        )}
      </Card>
    </div>
  )
}
